// src/features/auth/signup/hooks/useDuplicateCheck.ts

import {
  useState,
} from 'react';

import { signupService } from '../services/signupService';

import type {
  DuplicateField,
  DuplicateMessageMap,
  DuplicateStatusMap,
} from '../types/signup.types';

import {
  getErrorMessage,
  isDuplicatedResponse,
  validateDuplicateField,
} from '../utils/signup.utils';

const initialStatus: DuplicateStatusMap =
  {
    userId: 'unchecked',
    userNickname: 'unchecked',
  };

export const useDuplicateCheck =
  () => {
    const [
      duplicateStatus,
      setDuplicateStatus,
    ] =
      useState<DuplicateStatusMap>(
        initialStatus,
      );

    const [
      duplicateMessages,
      setDuplicateMessages,
    ] =
      useState<DuplicateMessageMap>(
        {},
      );

    const resetDuplicateField = (
      field: DuplicateField,
    ): void => {
      setDuplicateStatus(
        (previous) => ({
          ...previous,
          [field]: 'unchecked',
        }),
      );

      setDuplicateMessages(
        (previous) => ({
          ...previous,
          [field]: '',
        }),
      );
    };

    const checkDuplicate =
      async (
        field: DuplicateField,
        value: string,
      ): Promise<void> => {
        const validationMessage =
          validateDuplicateField(
            field,
            value,
          );

        if (validationMessage) {
          setDuplicateStatus(
            (previous) => ({
              ...previous,
              [field]:
                'unchecked',
            }),
          );

          setDuplicateMessages(
            (previous) => ({
              ...previous,
              [field]:
                validationMessage,
            }),
          );

          return;
        }

        const requestValue =
          value.trim();

        setDuplicateStatus(
          (previous) => ({
            ...previous,
            [field]: 'checking',
          }),
        );

        setDuplicateMessages(
          (previous) => ({
            ...previous,
            [field]:
              '중복 여부를 확인하고 있습니다.',
          }),
        );

        try {
          const response =
            await signupService.checkDuplicate(
              field,
              requestValue,
            );

          const duplicated =
            isDuplicatedResponse(
              response.data,
            );

          if (duplicated) {
            setDuplicateStatus(
              (previous) => ({
                ...previous,
                [field]:
                  'duplicate',
              }),
            );

            setDuplicateMessages(
              (previous) => ({
                ...previous,
                [field]:
                  response.data
                    .message ||
                  '이미 사용 중인 값입니다.',
              }),
            );

            return;
          }

          setDuplicateStatus(
            (previous) => ({
              ...previous,
              [field]:
                'available',
            }),
          );

          setDuplicateMessages(
            (previous) => ({
              ...previous,
              [field]:
                response.data
                  .message ||
                '사용할 수 있습니다.',
            }),
          );
        } catch (
          error: unknown
        ) {
          setDuplicateStatus(
            (previous) => ({
              ...previous,
              [field]:
                'unchecked',
            }),
          );

          setDuplicateMessages(
            (previous) => ({
              ...previous,
              [field]:
                getErrorMessage(
                  error,
                  '중복확인 중 오류가 발생했습니다.',
                ),
            }),
          );
        }
      };

    return {
      duplicateStatus,
      duplicateMessages,
      checkDuplicate,
      resetDuplicateField,
    };
  };